import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { ACTIVE_NETWORK } from '../config/network';
import CONTRACTS from '../contracts/addresses';
import StorageABI from '../contracts/StorageABI.json';
import LogicABI from '../contracts/LogicABI.json';
import USDTABI from '../contracts/USDTABI.json';

const Ctx = createContext();
export const useWeb3 = () => useContext(Ctx);

export function Web3Provider({ children }) {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contracts, setContracts] = useState({});
  const [userId, setUserId] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const initContracts = useCallback(async (s) => {
    const storage = new ethers.Contract(CONTRACTS.STORAGE, StorageABI, s);
    const logic = new ethers.Contract(CONTRACTS.LOGIC, LogicABI, s);
    const usdt = new ethers.Contract(CONTRACTS.USDT, USDTABI, s);
    setContracts({ storage, logic, usdt });
    return storage;
  }, []);

  const checkRegistration = useCallback(async (storage, addr) => {
    try {
      const uid = await storage.getUserIdByAddress(addr);
      const id = uid.toNumber();
      if (id > 0) { setUserId(id); setIsRegistered(true); }
      else { setUserId(null); setIsRegistered(false); }
    } catch { setUserId(null); setIsRegistered(false); }
  }, []);

  const switchNetwork = useCallback(async () => {
    try {
      await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: ACTIVE_NETWORK.chainId }] });
    } catch (e) {
      if (e.code === 4902) {
        await window.ethereum.request({ method: 'wallet_addEthereumChain', params: [{
          chainId: ACTIVE_NETWORK.chainId, chainName: ACTIVE_NETWORK.chainName,
          rpcUrls: ACTIVE_NETWORK.rpcUrls, blockExplorerUrls: ACTIVE_NETWORK.blockExplorerUrls,
          nativeCurrency: ACTIVE_NETWORK.nativeCurrency
        }] });
      }
    }
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) { alert('Install MetaMask'); return; }
    setIsConnecting(true);
    try {
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      await switchNetwork();
      const p = new ethers.providers.Web3Provider(window.ethereum, 'any');
      const s = p.getSigner();
      setProvider(p); setSigner(s); setAccount(accounts[0]);
      const storage = await initContracts(s);
      await checkRegistration(storage, accounts[0]);
    } catch (e) { console.error(e); }
    setIsConnecting(false);
  }, [switchNetwork, initContracts, checkRegistration]);

  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', (accs) => { if (accs.length > 0) connectWallet(); else { setAccount(null); setIsRegistered(false); } });
      window.ethereum.on('chainChanged', () => connectWallet());
      window.ethereum.request({ method: 'eth_accounts' }).then(accs => { if (accs.length > 0) connectWallet(); });
    }
  }, [connectWallet]);

  return (<Ctx.Provider value={{ account, provider, signer, contracts, userId, isRegistered, isConnecting, connectWallet, setIsRegistered, setUserId }}>
    {children}
  </Ctx.Provider>);
}
